import type { ReactElement } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Capability } from '@boud/contracts';
import { useAuth } from '../auth/AuthProvider';

/** Shown while the refresh cookie is exchanged for an access token on load. */
function RestoringSession(): ReactElement {
  const { t } = useTranslation();
  return (
    <div className="login-shell">
      <div className="empty" role="status" aria-live="polite">
        {t('restoring_session')}
      </div>
    </div>
  );
}

/**
 * Requires a session.
 *
 * The originating path is remembered, so a user who followed a notification
 * deep-link and had to sign in lands where they meant to go rather than on a
 * generic dashboard.
 */
export function RequireSession(): ReactElement {
  const { user, isRestoring } = useAuth();
  const location = useLocation();

  if (isRestoring) return <RestoringSession />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  return <Outlet />;
}

/**
 * Requires a capability for a screen (REQ-070).
 *
 * When a role change removes access to the screen the user is currently on,
 * their next navigation falls back to the Dashboard — which every role can see
 * — rather than showing an error page.
 *
 * This is convenience, not security. The server refuses the underlying request
 * with 403 whatever the client renders; this only spares the user a screen full
 * of failed panels.
 */
export function RequireCapability({ capability }: { capability: Capability }): ReactElement {
  const { can } = useAuth();

  if (!can(capability)) return <Navigate to="/dashboard" replace />;

  return <Outlet />;
}
